import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { Button, Card, LoadingOverlay, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import DataView from './components/DataView';
import { ServiceType } from '../../../../models/core/services.type';
import { Filter, FilterData } from './components/Filter';
import PageContent from '../../../../components/core/PageContent/PageContent';
import servicesService from '../../../../services/core/services.service';
import { Feature, SessionStorageKey } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

function ServiceSearch() {
  const navigate = useNavigate();

  const [data, setData] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    const cacheResult = JSON.parse(
      sessionStorage.getItem(SessionStorageKey.ServiceSearch) || '[]'
    ) as ServiceType[];

    if (cacheResult.length > 0) {
      setData(cacheResult);
    } else {
      filter({ servico: '' });
    }
  }, []);

  const clear = () => {
    sessionStorage.removeItem(SessionStorageKey.ServiceSearch);
    setData([]);
  };

  const filter = async (inputFilterData: FilterData) => {
    try {
      setLoading(true);
      const result = await servicesService.search({
        servico: inputFilterData?.servico || null,
        limit: 1000,
        offset: 0,
      });
      sessionStorage.setItem(SessionStorageKey.ServiceSearch, JSON.stringify(result));
      setData(result);
    } catch (error: any) {
      showNotification({
        title: 'Serviços',
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar serviços.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmActionResult = async (item: ServiceType, action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'excluir':
          await servicesService.delete({ idServico: item.idServico });
          setData(data.filter((x) => x.idServico !== item.idServico));
          break;
        case 'visualizar':
          navigate(`/services/${item.idServico}`, {
            state: {
              service: item,
            },
          });
          return;
        case 'editar':
          navigate(`/services/${item.idServico}/edit`, {
            state: {
              service: item,
            },
          });
          return;
        default:
          break;
      }

      showNotification({
        title: 'Serviços',
        message: `Serviço excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Serviços',
        message: error?.isBusinessException ? error.description : `Não foi possível ${action} o serviço.`,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Service}
        title="Serviços"
        description="Gerencie serviços."
        buttons={[
          <Button
            key="Adicionar"
            color="primary"
            leftSection={<IconPlus size={18} />}
            disabled={loading}
            onClick={() => navigate('/services/add')}
            data-permission={Permission.ServiceAdd}
          >
            Adicionar
          </Button>,
        ]}
      />
      <PageContent>
        <Filter filter={filter} clear={clear} loading={loading} />
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Paper shadow="xs" p="md" withBorder>
            <DataView data={data} confirmActionResult={confirmActionResult} />
          </Paper>
        </div>
      </PageContent>
    </Card>
  );
}

export default ServiceSearch;
